import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Save, Trash2, X } from 'lucide-react';
import { AdminShell } from '@/components/admin-shell';
import { FgCard } from '@/components/fg-card';
import { FgButton } from '@/components/fg-button';
import { FgInput } from '@/components/fg-input';
import { FgToast } from '@/components/fg-toast';
import { api } from '@/lib/api';

/* ────────────────────────────────────────────────────────────────────────── */

interface ConfigRow {
  id: string;
  key: string;
  value: unknown;
  description: string | null;
  updatedAt: string;
}

/**
 * Curated editor for `video.free_preview_lesson_ids` — the most common reason
 * to come to this screen. Renders as a list of lesson UUIDs with add/remove,
 * underneath the generic JSON editor.
 */
const FREE_PREVIEW_KEY = 'video.free_preview_lesson_ids';

export function ConfigPage() {
  const qc = useQueryClient();
  const rows = useQuery<ConfigRow[]>({
    queryKey: ['admin', 'platform-config'],
    queryFn: async () => (await api.get('/api/admin/platform-config')).data,
  });

  const [editing, setEditing] = useState<ConfigRow | null>(null);
  const [creating, setCreating] = useState<{ key: string } | null>(null);

  const remove = useMutation({
    mutationFn: async (key: string) => (await api.delete(`/api/admin/platform-config/${key}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'platform-config'] });
      FgToast.success('Config row deleted');
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? 'Delete failed'),
  });

  return (
    <AdminShell>
      <FreePreviewWidget rows={rows.data ?? []} />

      <FgCard className="mt-6 p-5">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">All platform-config rows</h2>
            <p className="mt-1 text-xs text-gray-600">
              Anything not on the curated widget below is edited as JSON. Mind the schema — the
              mobile app reads these values at runtime.
            </p>
          </div>
          <FgButton
            onClick={() => setCreating({ key: '' })}
            size="sm"
          >
            <Plus size={14} /> New row
          </FgButton>
        </div>
        <div className="flex flex-col gap-2">
          {(rows.data ?? []).map((row) => (
            <FgCard key={row.id} className="flex items-center justify-between p-3 fg-anim-fast">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="rounded bg-black px-2 py-0.5 font-mono text-xs font-bold text-yellow-400">
                    {row.key}
                  </span>
                  <span className="text-xs text-gray-500">
                    {row.description ?? 'no description'}
                  </span>
                </div>
                <pre className="mt-1 max-w-full overflow-x-auto rounded-md bg-[#0A0A0A] p-2 font-mono text-[11px] text-black">
                  {JSON.stringify(row.value, null, 2)}
                </pre>
              </div>
              <div className="flex gap-2 pl-4">
                <FgButton size="sm" variant="outline" onClick={() => setEditing(row)}>
                  Edit
                </FgButton>
                <FgButton
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (confirm(`Delete config row "${row.key}"?`)) remove.mutate(row.key);
                  }}
                >
                  <Trash2 size={14} />
                </FgButton>
              </div>
            </FgCard>
          ))}
          {rows.data?.length === 0 && (
            <p className="rounded-md bg-[#0A0A0A] p-4 text-sm text-gray-500">
              No config rows yet.
            </p>
          )}
        </div>
      </FgCard>

      {(editing || creating) && (
        <ConfigRowEditor
          row={editing}
          initialKey={creating?.key}
          onClose={() => {
            setEditing(null);
            setCreating(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['admin', 'platform-config'] });
            setEditing(null);
            setCreating(null);
          }}
        />
      )}
    </AdminShell>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

function FreePreviewWidget({ rows }: { rows: ConfigRow[] }) {
  const qc = useQueryClient();
  const row = rows.find((r) => r.key === FREE_PREVIEW_KEY);
  const ids: string[] = useMemo(
    () => (Array.isArray(row?.value) ? (row!.value as string[]) : []),
    [row],
  );

  const [draft, setDraft] = useState('');

  const upsert = useMutation({
    mutationFn: async (next: string[]) =>
      (
        await api.post(`/api/admin/platform-config/${FREE_PREVIEW_KEY}`, {
          value: next,
          description: 'Lesson IDs the mobile app may play without a subscription',
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'platform-config'] });
      FgToast.success('Free preview list updated');
      setDraft('');
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? 'Save failed'),
  });

  return (
    <FgCard className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-base font-semibold">Free-preview lessons</h2>
          <p className="mt-1 text-xs text-gray-600">
            Lessons in this list are unlocked for every learner — even without a subscription.
            They complement the per-level / per-lesson <code className="rounded bg-[#1C1C1E] px-1 text-xs">is_free_preview</code> toggle.
          </p>
        </div>
        <span className="rounded bg-yellow-400 px-2 py-0.5 text-xs font-bold text-black">
          {ids.length} lessons
        </span>
      </div>
      <div className="mt-4 flex gap-2">
        <FgInput
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Paste a lesson UUID"
          className="flex-1"
        />
        <FgButton
          onClick={() => {
            if (!draft) return;
            upsert.mutate([...ids, draft]);
          }}
        >
          <Plus size={14} /> Add
        </FgButton>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {ids.map((id) => (
          <span
            key={id}
            className="inline-flex items-center gap-1 rounded-full bg-red-50 px-3 py-1 text-xs font-mono text-red-500"
          >
            {id.slice(0, 8)}…
            <button
              onClick={() => upsert.mutate(ids.filter((x) => x !== id))}
              className="ml-1 rounded-full p-0.5 hover:bg-red-100"
            >
              <X size={12} />
            </button>
          </span>
        ))}
        {ids.length === 0 && (
          <p className="text-xs text-gray-500">No free-preview lessons set.</p>
        )}
      </div>
    </FgCard>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

function ConfigRowEditor({
  row,
  initialKey,
  onClose,
  onSaved,
}: {
  row: ConfigRow | null;
  initialKey?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [key, setKey] = useState(row?.key ?? initialKey ?? '');
  const [description, setDescription] = useState(row?.description ?? '');
  const [text, setText] = useState(JSON.stringify(row?.value ?? {}, null, 2));
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      let value: unknown;
      try {
        value = JSON.parse(text);
        setErr(null);
      } catch (e) {
        setErr((e as Error).message);
        throw e;
      }
      return (
        await api.post(`/api/admin/platform-config/${encodeURIComponent(key)}`, {
          value,
          description: description || null,
        })
      ).data;
    },
    onSuccess: () => {
      FgToast.success('Saved');
      onSaved();
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? 'Save failed'),
  });

  return (
    <FgCard className="mt-6 p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">
          {row ? `Editing ${row.key}` : 'New config row'}
        </h2>
        <button onClick={onClose} className="rounded-md p-2 text-[#8E8E93] hover:bg-[#1C1C1E] fg-anim-fast">
          <X size={16} />
        </button>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3">
        <FgInput
          label="Key"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="e.g. video.free_preview_lesson_ids"
          required
          disabled={!!row}
          hint={row ? 'Key is immutable — delete + recreate to rename' : undefined}
        />
        <FgInput
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div>
          <label className="text-xs font-semibold text-gray-700">Value (JSON)</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
            className="mt-1 h-80 w-full rounded-md border border-[rgba(255,255,255,0.1)] bg-[#0A0A0A] p-3 font-mono text-xs focus:border-red-500 focus:outline-none"
          />
          {err ? (
            <p className="mt-1 text-xs text-error">⚠ {err}</p>
          ) : (
            <p className="mt-1 text-xs text-success">✓ valid JSON</p>
          )}
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <FgButton variant="ghost" onClick={onClose}>
          Cancel
        </FgButton>
        <FgButton onClick={() => save.mutate()} loading={save.isPending}>
          <Save size={14} /> Save
        </FgButton>
      </div>
    </FgCard>
  );
}